import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  Box,
  Grid,
  CardHeader,
  Typography,
  FormLabel,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import api from "../../axios/api";
import { useSelector } from "react-redux";
import { showNotification } from "../../redux/slices/notificationSlice";
import CustomButton from "../../componentsII/Buttons";

// Validation schema using Yup
const schema = Yup.object().shape({
  veh_name_code: Yup.string().required("Vehicle name code is required"),
  year: Yup.string().required("Year is required"),
  make_id: Yup.string().required("Make is required"),
  model_id: Yup.string().required("Model is required"),
  veh_datatype_available: Yup.string().required(
    "Vehicle data type is required"
  ),
  veh_type_id: Yup.string().required("Vehicle type is required"),
  veh_subtype_id: Yup.string().required("Vehicle subtype is required"),
  comp_location_id: Yup.string().required("location is required"),
});

const VehicleForm = ({ onClose, refetch }) => {
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicleSubtypes, setVehicleSubtypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [years, setYears] = useState([]);
  const vehicleDataTypes = [
    {
      id: 1,
      value: "DISTANCE_BASE",
      label: "DISTANCE",
    },
    {
      id: 2,
      value: "FUEL_BASE",
      label: "FUEL",
    },
  ];

  const { user } = useSelector((state) => state);
  const year = useSelector((state) => state.user.selectedYear);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      id: 0,
      veh_name_code: "",
      company_id: user?.user?.company?.id,
      comp_data_year_id: user?.selectedYear?.id,
      year: user?.selectedYear?.id,
      make_id: "",
      model_id: "",
      veh_datatype_available: "",
      veh_type_id: "",
      veh_subtype_id: "",
      comp_location_id: ""
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [makesRes, vehicleTypesRes] = await Promise.all([
          api.get("/vehicle/vehiclemake"),
          api.get("/vehicle/vehicleType"),
        ]);
        const YearsRes = await api.get(`/company/companydatayear?company_id=${user.user.company.id}`)

        setMakes(makesRes.data.data);
        setVehicleTypes(vehicleTypesRes.data.data);
        setYears(YearsRes.data.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    const fetchLocations = async () => {
      const response = await api(
        `/company/companylocation?company_id=${user.user.company.id}&year_id=${year.id}`
      );
      setLocations(response.data.data);
    };

    fetchLocations();
    fetchData();

  }, []);

  const handleMakeChange = async (makeId) => {
    if (makeId) {
      try {
        const res = await api.get(
          `/vehicle/vehiclemodel?vehicle_make_id=${makeId}`
        );
        setModels(res.data.data);
      } catch (error) {
        console.error("Error fetching models:", error);
      }
    }
  };

  const handleTypeChange = async (typeId) => {
    if (typeId) {
      try {
        const res = await api.get(
          `/vehicle/vehiclesubtype?vehicle_type_id=${typeId}`
        );
        setVehicleSubtypes(res.data.data);
      } catch (error) {
        console.error("Error fetching models:", error);
      }
    }
  };

  // Submit form data
  const onSubmit = async (data) => {
    const body = { ...data };
    try {
      const response = await api.post("/vehicle/addUpdateCompanyVehicle", body);
      if (response.data.status === "200") {
        showNotification(response.data.message, "success");
        refetch();
        onClose();
      } else {
        showNotification(response.data.message, "error");
        onClose();
      }
    } catch (error) {
      showNotification(error.response.data.message, "error");
      onClose();
      console.error("Error submitting form:", error);
    }
  };

  const handleReset = () => {
    reset();
  };

  return (
    <Box sx={{ maxWidth: 600, margin: "auto" }}>
      <CardHeader title={<Typography variant="h5">Add Vehicle</Typography>} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          {/* Vehicle Name Code */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Controller
                  name="veh_name_code"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={
                        <span>
                          Vehicle Name
                          <span style={{ color: 'red' }}> *</span>
                        </span>
                      }
                      fullWidth
                      error={!!errors.veh_name_code}
                      helperText={errors.veh_name_code?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name="comp_location_id"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label={
                        <span>
                          Location
                          <span style={{ color: 'red' }}> *</span>
                        </span>
                      }
                      fullWidth
                      error={!!errors.comp_location_id}
                      helperText={errors.comp_location_id?.message}
                    >
                      {locations.map((location) => (
                        <MenuItem key={location?.id} value={location.id}>
                          {location.loc_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Controller
                  name="year"
                  control={control}
                  render={({ field }) => (

                    <TextField
                      {...field}
                      select
                      label={
                        <span>
                          Year
                          <span style={{ color: 'red' }}> *</span>
                        </span>
                      }
                      placeholder="Select Year"
                      fullWidth
                      error={!!errors.year}
                      helperText={errors.year?.message}
                    >
                      <MenuItem value="" disabled>
                        Select Year
                      </MenuItem>
                      {years.map((year) => (
                        <MenuItem key={year.id} value={year.id}>
                          {year.start_year}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="veh_datatype_available"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      error={!!errors.veh_datatype_available}
                    >
                      <InputLabel>Vehicle Data Type<span style={{ color: 'red' }}> *</span></InputLabel>
                      <Select {...field} label="Vehicle Data Type">
                        {vehicleDataTypes.map((type) => (
                          <MenuItem key={type.id} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.veh_datatype_available && (
                        <p className="text-xs text-red-500">{errors.veh_datatype_available.message}</p>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Controller
                  name="make_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.make_id}>
                      <InputLabel>Make<span style={{ color: 'red' }}> *</span></InputLabel>
                      <Select
                        {...field}
                        label="Make"
                        onChange={(e) => {
                          field.onChange(e);
                          handleMakeChange(e.target.value);
                        }}
                      >
                        {makes.map((make) => (
                          <MenuItem key={make.value} value={make.value}>
                            {make.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.make_id && <p className="text-xs text-red-500">{errors.make_id.message}</p>}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="veh_type_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.veh_type_id}>
                      <InputLabel>Vehicle Type<span style={{ color: 'red' }}> *</span></InputLabel>
                      <Select
                        {...field}
                        label="Vehicle Type"
                        onChange={(e) => {
                          field.onChange(e);
                          handleTypeChange(e.target.value);
                        }}
                      >
                        {vehicleTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.veh_type_id && (
                        <p className="text-xs text-red-500">{errors.veh_type_id.message}</p>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Controller
                  name="model_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.model_id}>
                      <InputLabel>Model<span style={{ color: 'red' }}> *</span></InputLabel>
                      <Select
                        {...field}
                        label="Model"
                        disabled={models.length === 0}
                      >
                        {models.map((model) => (
                          <MenuItem key={model.value} value={model.value}>
                            {model.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.model_id && <p className="text-xs text-red-500">{errors.model_id.message}</p>}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name="veh_subtype_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.veh_subtype_id}>
                      <InputLabel>Vehicle Subtype<span style={{ color: 'red' }}> *</span></InputLabel>
                      <Select
                        {...field}
                        label="Vehicle Subtype"
                        disabled={vehicleSubtypes.length === 0}
                      >
                        {vehicleSubtypes.map((subtype) => (
                          <MenuItem key={subtype.value} value={subtype.value}>
                            {subtype.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.veh_subtype_id && <p className="text-xs text-red-500">{errors.veh_subtype_id.message}</p>}
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </Grid>


          <Grid item xs={12}>
            <div className="flex gap-4 justify-end">
              <CustomButton
                type="button"
                className="bg-gray-500 hover:bg-gray-700 text-white"
                click={handleReset}
                text="Reset"
              />
              <CustomButton
                type="submit"
                className="bg-green-600 hover:bg-green-700 text-white"
                text="Submit"
              />
            </div>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default VehicleForm;
